/*
============================================================
 BigOpolis Fill-In Puzzle Game
------------------------------------------------------------
 Main application entry point

 Description:
 This is a fill-in puzzle game (word-fit/criss-cross) built 
 with vanilla JavaScript and Vite. Players fit words into a 
 crossword-style grid using spatial reasoning; no definition 
 clues provided. Features include a multi-layer grid, clue 
 boards, progress tracking, timer, and a state machine for 
 game flow.

 Author: Alex Popadich
 License: MIT (see LICENSE)
============================================================
*/

// Settings menu action stubs
export class SettingsMenuHandler {
  // Font size controls
  static changeFontSize(delta) {
    const root = document.documentElement;
    // Get current --base-font-size or default to 14
    const currentSize = parseFloat(
      getComputedStyle(root).getPropertyValue("--base-font-size") || "14",
    );
    const newSize = Math.max(10, Math.min(20, currentSize + delta));
    root.style.setProperty("--base-font-size", `${newSize}px`);
    console.log(`[Settings] Font size changed to ${newSize}px`);
  }

  static setLightMode() {
    console.log("[Settings] Light mode selected: applying light color scheme");
    document.body.classList.remove("dark-mode");
    document.body.classList.add("light-mode");
  }

  static setDarkMode() {
    console.log("[Settings] Dark mode selected: applying dark color scheme");
    document.body.classList.remove("light-mode");
    document.body.classList.add("dark-mode");
  }

  static increaseFont() {
    console.log("[Settings] Font size increased");
    SettingsMenuHandler.changeFontSize(1);
  }

  static decreaseFont() {
    console.log("[Settings] Font size decreased");
    SettingsMenuHandler.changeFontSize(-1);
  }
}

import "../css/fingame.css";
import "../css/style.css";

import { FinGame } from "./FinGame.js";
import { setupStateMachine } from "./StateMachineIntegration.js";
import { DataLoader } from "./api/DataLoader.js";
import { URLParameterParser } from "./api/URLParameterParser.js";

window.addEventListener("DOMContentLoaded", async () => {
  let puzzleName = "None";
  let displayTitle = "Puzzle"; // Default display title

  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const nowLocaleString = now.toLocaleString("en-US", {
      timeZone: userTimezone,
    });
    console.log(`Current date and time (${userTimezone}):`, nowLocaleString);

    const dataPath = import.meta.env.VITE_DATA_PATH || "./data/daily";
    console.log("[Main] Data path:", dataPath);

    // === EARLY TITLE SETUP ===
    // Set title IMMEDIATELY from URL/storage before any async operations
    // This ensures GTM page view has a meaningful title
    let selectedPuzzle = null;
    let selectedSourceType = null;

    (() => {
      try {
        // Use centralized URL parameter parser
        const sourceInfo = URLParameterParser.extractSourceId();
        selectedPuzzle = sourceInfo.sourceId;
        selectedSourceType = sourceInfo.sourceType;
        URLParameterParser.extractReturnUrl();

        if (selectedPuzzle) {
          window.SELECTED_PUZZLE_FILENAME = selectedPuzzle;
          console.log("[Main] Selected puzzle:", selectedPuzzle, {
            sourceType: selectedSourceType,
          });

          // Set INITIAL title immediately from puzzle name
          const match = selectedPuzzle.match(/^(\d{4})(\d{2})(\d{2})$/);
          if (match) {
            const year = parseInt(match[1], 10);
            const month = parseInt(match[2], 10);
            const day = parseInt(match[3], 10);
            const dateObj = new Date(year, month - 1, day);
            if (!isNaN(dateObj.getTime())) {
              displayTitle = dateObj.toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
                weekday: "long",
              });
            } else {
              displayTitle = selectedPuzzle;
            }
          } else {
            displayTitle = selectedPuzzle;
          }

          // SET TITLE IMMEDIATELY (before puzzle loads)
          document.title = `BigOpolis Fill-it-In: ${displayTitle}`;
          console.log("[Main] Early title set:", document.title);
        } else {
          // No puzzle selected - use default
          document.title = "BigOpolis Fill-it-In: Daily Puzzle";
          console.log("[Main] Default title set (no puzzle selected)");
        }
      } catch (err) {
        console.error("[Main] Error reading selected puzzle:", err);
        document.title = "BigOpolis Fill-it-In: NO DATA";
      }
    })();

    // Initialize dataLayer for GTM (if not already initialized by GTM snippet)
    window.dataLayer = window.dataLayer || [];

    // Send initial page view event with early title
    window.dataLayer.push({
      event: "page_view_early",
      page_title: document.title,
      puzzle_name: selectedPuzzle || "default",
      title_source: "url_parameter",
    });

    let finJsonData = null;

    console.log("[Loader] Starting puzzle load sequence...");
    console.log("[Loader] Selected puzzle:", selectedPuzzle || "none");

    // === DATA LOADING USING UNIFIED DATALOADER ===
    // Instead of manual phase-based loading, use the DataLoader factory
    // which abstracts the data source and handles fallback chain automatically
    try {
      const dataLoader = new DataLoader({
        apiHost: import.meta.env.VITE_API_HOST_URL || "",
        dataPath: import.meta.env.VITE_DATA_PATH || "./data/daily",
        defaultPuzzle: "fillitin_default",
        userTimezone,
      });

      // Load puzzle data using unified interface
      finJsonData = await dataLoader.load(selectedPuzzle, selectedSourceType);

      console.log("[Loader] Puzzle load sequence complete");
    } catch (error) {
      console.error("[Loader] Failed to load puzzle data:", error.message);
      throw new Error(`Critical: No puzzle data available: ${error.message}`);
    }

    // Set puzzleSubfolder for PDF file location based on source type
    // This is done BEFORE finGame is initialized
    let puzzleSubfolder = "";
    if (selectedSourceType === "sample") {
      puzzleSubfolder = "sample";
    } else if (selectedSourceType === "archive") {
      puzzleSubfolder = "archive";
    }
    console.log(
      "[Main] Puzzle subfolder for PDFs:",
      puzzleSubfolder || "(root)",
    );

    // === FINAL TITLE UPDATE ===
    // Update title with VERIFIED puzzle metadata from loaded JSON
    if (
      finJsonData &&
      finJsonData.metadata &&
      finJsonData.metadata.puzzleName
    ) {
      puzzleName = finJsonData.metadata.puzzleName;
      let finalDisplayTitle = displayTitle; // Start with early title

      try {
        const m = puzzleName.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (m) {
          const year = parseInt(m[1], 10);
          const month = parseInt(m[2], 10);
          const day = parseInt(m[3], 10);
          const dateObj = new Date(year, month - 1, day);
          if (!isNaN(dateObj.getTime())) {
            finalDisplayTitle = dateObj.toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              weekday: "long",
            });
          } else {
            finalDisplayTitle = puzzleName;
          }
        } else {
          finalDisplayTitle = puzzleName;
        }

        document.querySelector("#puzzle-title").textContent =
          "Puzzle: " + finalDisplayTitle;

        // UPDATE FINAL TITLE with verified metadata
        document.title = `BigOpolis Fill-it-In: ${finalDisplayTitle}`;
        console.log("[Main] Final title set from metadata:", document.title);

        // Send GTM event for title update (after puzzle loads)
        window.dataLayer.push({
          event: "puzzle_loaded",
          page_title: document.title,
          puzzle_name: puzzleName,
          title_source: "puzzle_metadata",
        });
      } catch (err) {
        console.warn("[Main] Error parsing puzzleName:", err);
        document.querySelector("#puzzle-title").textContent = puzzleName;
        document.title = `BigOpolis Fill-it-In: ${puzzleName}`;
      }
    } else {
      // No puzzle metadata - keep early title
      document.title = document.title || "BigOpolis Fill-it-In: Puzzle";
      console.log("[Main] No metadata available, using early title");
    }

    // Initialize game with finJsonData (either from API or default)
    if (finJsonData && finJsonData.finmodel) {
      if (!window.finGame) {
        window.CLUEBOARD_DEBUG_LOG = false;
        window.FINGAME_DEBUG_LOG = false;
        window.finGame = new FinGame();
        const canvas = document.querySelector("#fillincanvas");
        const clueBoard1 = document.querySelector("#clueboard1");
        const clueBoard2 = document.querySelector("#clueboard2");
        const clueBoard3 = document.querySelector("#clueboard3");
        const clueBoard4 = document.querySelector("#clueboard4");
        const clueBoard5 = document.querySelector("#clueboard5");
        const clueBoard6 = document.querySelector("#clueboard6");
        const statusElem = document.querySelector("#game-status");
        const versionElem = document.querySelector("#app-version");
        const startButton = document.querySelector("#play-btn");
        const timerButton = document.querySelector("#game-timer");
        const cluesContainer = document.querySelector("#clues-container");

        const clueBoardsArr = [
          clueBoard1,
          clueBoard2,
          clueBoard3,
          clueBoard4,
          clueBoard5,
          clueBoard6,
        ];

        const options = {
          canvas,
          clueBoardsArr,
          statusElem,
          versionElem,
          startButton,
          timerButton,
          cluesContainer,
        };

        const fmodel = finJsonData.finmodel;
        // Access the version string
        const appVersion = import.meta.env.APP_VERSION;

        console.log(`[Main] Application loaded: v${appVersion}`);

        versionElem.innerText = import.meta.env.VITE_APP_VERSION;
        document.querySelector("#puzzle-size").textContent =
          `${fmodel.rowcount}x${fmodel.columncount}`;
        document.querySelector("#word-count").textContent =
          `${fmodel.wordcount} words`;
        document.querySelector("#puzzle-category").textContent = fmodel.type;

        window.finGame.setupGame(options);

        // Set the puzzle subfolder for PDF file location
        window.finGame.puzzleSubfolder = puzzleSubfolder;

        // After window.finGame is created
        const stateMachine = setupStateMachine(window.finGame);

        // Optionally transition to initial state
        stateMachine.transition("LOADING");

        // TODO: There needs to be some decision about what to do if data does not load
        window.finGame.loadJsonData(finJsonData);

        // After puzzle data loads the game is ready to play
        stateMachine.transition("READY");

        document
          .querySelector("#fillincanvas")
          .addEventListener("click", (event) => {
            if (window.finGame && window.finGame.gameController) {
              window.finGame.gameController.handleCanvasClick(event);
            }
          });

        window.addEventListener("keydown", (event) => {
          if (window.finGame && window.finGame.gameController) {
            window.finGame.gameController.handleEvent(event);
          }
        });

        // Attach global handlers for settings button menu (if running in browser)
        if (typeof window !== "undefined") {
          window.handleSettingsMenu = function (action) {
            switch (action) {
              case "light-mode":
                SettingsMenuHandler.setLightMode();
                break;
              case "dark-mode":
                SettingsMenuHandler.setDarkMode();
                break;
              case "font-larger":
                SettingsMenuHandler.increaseFont();
                break;
              case "font-smaller":
                SettingsMenuHandler.decreaseFont();
                break;
              default:
                console.log("[Settings] Unknown menu action:", action);
            }
          };
        }

        // === Responsive Design Detection (Option 1) ===
        const setupResponsiveDetection = () => {
          // Define breakpoints matching layout.css
          const breakpoints = {
            mobile: window.matchMedia("(max-width: 480px)"),
            tablet: window.matchMedia("(max-width: 768px)"),
            desktop: window.matchMedia("(min-width: 769px)"),
          };

          // Handler for mobile breakpoint
          const handleMobileChange = (e) => {
            if (e.matches) {
              console.log("[Responsive] Mobile layout activated (<480px)");
              document.body.setAttribute("data-layout", "mobile");

              // Set responsive mode for cell size constraints
              if (window.finGame?.setResponsiveMode) {
                window.finGame.setResponsiveMode("mobile");
              }

              // Enable compact UI mode for mobile
              if (window.finGame?.setCompactMode) {
                window.finGame.setCompactMode(true);
              }
            }
          };

          // Handler for tablet breakpoint
          const handleTabletChange = (e) => {
            if (e.matches && !breakpoints.mobile.matches) {
              console.log("[Responsive] Tablet layout activated (481-768px)");
              document.body.setAttribute("data-layout", "tablet");

              // Set responsive mode for cell size constraints
              if (window.finGame?.setResponsiveMode) {
                window.finGame.setResponsiveMode("tablet");
              }

              // Disable compact mode (show all UI)
              if (window.finGame?.setCompactMode) {
                window.finGame.setCompactMode(false);
              }
            }
          };

          // Handler for desktop breakpoint
          const handleDesktopChange = (e) => {
            if (e.matches) {
              console.log("[Responsive] Desktop layout activated (>768px)");
              document.body.setAttribute("data-layout", "desktop");

              // Set responsive mode for cell size constraints
              if (window.finGame?.setResponsiveMode) {
                window.finGame.setResponsiveMode("desktop");
              }

              // Disable compact mode (show all UI)
              if (window.finGame?.setCompactMode) {
                window.finGame.setCompactMode(false);
              }
            }
          };

          // Attach listeners using modern API
          breakpoints.mobile.addEventListener("change", handleMobileChange);
          breakpoints.tablet.addEventListener("change", handleTabletChange);
          breakpoints.desktop.addEventListener("change", handleDesktopChange);

          // Run handlers once on load to set initial state
          handleMobileChange(breakpoints.mobile);
          handleTabletChange(breakpoints.tablet);
          handleDesktopChange(breakpoints.desktop);

          console.log("[Responsive] Breakpoint detection initialized");
        };

        // Initialize responsive detection after game setup
        setupResponsiveDetection();

        // === Clue List Item Click Handler ===
        // Listen for clicks on dynamically created clue list items
        if (cluesContainer) {
          cluesContainer.addEventListener("click", (event) => {
            const clueItem = event.target.closest(".clues-list li");
            if (clueItem) {
              const clueText = clueItem.textContent.trim();
              console.log("[Main] Clue item clicked:", {
                clueText: clueText,
                element: clueItem,
              });

              // Dispatch custom event for game to handle
              if (window.finGame) {
                window.finGame.handleClueItemClick(clueText, clueItem);
              }
            }
          });
          console.log("[Main] Clue list item click handler initialized");
        }
      }
    }

    // === Logo Click Handler ===
    // Origin-aware routing: Returns to the original page if one was tracked
    // Otherwise defaults to daily_board.html in production
    const logoLink = document.querySelector("#app-logo");
    if (logoLink) {
      logoLink.addEventListener("click", (e) => {
        e.preventDefault();

        const returnTo = URLParameterParser.getStoredReturnUrl();
        if (returnTo) {
          // User came from another page - return there
          window.location.href = returnTo;
          console.log("[Main] Returning to origin URL:", returnTo);
        } else if (import.meta.env.PROD) {
          // No origin tracked - use production default
          window.location.href = "/BigOp/fill-it-ins/daily_board.html";
          console.log("[Main] Returning to default board");
        } else {
          // Development: stay on page (no-op)
          console.log("[Main] Logo clicked in development mode");
        }
      });
    }
  } catch (e) {
    document.querySelector("#puzzle-title").textContent =
      `Error ${e} loading:` + puzzleName;
    console.error("[Main] Fatal error:", e);
  }
});
